<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Gamulation"
  model-name="game"
  param="gameId"
  page-prefix="/blade/games"
  :create="createGame"
  :fetch="fetchGame"
  :fetch-types="getGameTypes"
  :update="updateGame"
  :remove="deleteGame"
  deep-delete-collection="games"
  :enum-names="['games', 'environments', 'challenges', 'eventPools', 'characters', 'variants', 'metrics', 'outros', 'roles', 'narrativeTypes', 'speakers']"
  :stub="gameStub"
  :validator="validator"
  :utils="utils"
  :hide-buttons="!canManageGames"
  :show-submit="canEditGameGeneralSettings"
>
<template slot-scope="{ model: game, enums: { games, environments, challenges, eventPools, characters, variants, metrics, outros, roles, narrativeTypes, speakers }, utils: { unused }, validator }">
  <div style="margin-bottom: 15px; height: 3px; width: 100%; background-color: black;"/>
  <text-input v-if="!creating" :disabled="true" label :value="clientInviteLink(game)" copyable no-count>
    Client Invite Link
  </text-input>
  <button v-if="!creating" class="btn" @click.prevent="(ev) => generateInvite(game)" style="padding: 0 5px;">
    Generate Invite Link &amp; Invalidate Any Existing Invite Link
  </button>
  <template v-if="!creating && game.invites !== null && game.invites.length > 0">
    <div v-for="(invite, idx) in game.invites" :key="idx">
      <checkbox-input v-model="invite.skipEmailVerification">Skip Email Verification</checkbox-input>
    </div>
  </template>
  <div v-if="!creating && canManageUsers" style="margin: 15px 0; height: 3px; width: 100%; background-color: black;"/>
  <div v-if="!creating && canManageUsers">
    <h5>Gamulation Roster</h5>
    <!-- {{gameUsers}} -->
    <div>Total Count: {{gameUsers.length}}</div>
    <b-input-group>
      <b-input-group-prepend style="margin: 5px 5px 5px 0;">
        Filter:
      </b-input-group-prepend>
      <b-input v-model="filter"/>
    </b-input-group>
    <div>Filtered Count: {{formattedUsers.length}}</div>
    <b-table
      sort-by="lastName"
      :sort-desc="true"
      :items="formattedUsers"
      :fields="tableFields"
      :current-page="currentPage"
      :per-page="perPage"
      striped
      hover
      style="width: 100%; font-size: 12px;"
    >
    </b-table>
    <nav>
      <b-pagination
        size="sm"
        :total-rows="formattedUsers.length"
        :per-page="perPage"
        v-model="currentPage"
        prev-text="Prev"
        next-text="Next"
      />
    </nav>
    <b-input-group style="width: 30%; font-size: 80%;">
      <b-input-group-prepend style="margin: 8px 5px 5px 0;">
        Rows per page:
      </b-input-group-prepend>
      <b-input v-model="perPageInput" style="transform: scaleY(0.84); margin-bottom: 5px;"/>
      <b-input-group-append>
        <button class="btn" @click.prevent="updatePerPage" style="transform: scale(0.82) translate(-9%, -2%); margin-bottom: 5px;">Update</button>
      </b-input-group-append>
    </b-input-group>
  </div>
  <template v-if="canManageGames || canEditGameGeneralSettings">
  <div v-if="!creating" style="margin: 15px 0; height: 3px; width: 100%; background-color: black;"/>

  <!-- {{ game }} -->
  <!-- {{ metrics }} -->
  <!-- {{ unusedTeamMetricOptions }} -->
  <!-- {{ fetchedGame }} -->

  <text-input label v-model.trim="game.name" :validator="validator.name" required autofocus>Name</text-input>

  <collapsible-section
    name="Gamulation General Settings"
    id="GGS"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <text-input label v-model.trim="game.title" :validator="validator.title" required>Title</text-input>

    <checkbox-input
      v-model="game.titleOverridesClass"
    >Gamulation Title overrides Course Name on Gamulation Title Screen</checkbox-input>

    <text-input label v-model.trim="game.ticketPrefix">Ticket Prefix (optional)</text-input>
    <select-input
      label
      v-model="game.singleplayerGame"
      :options="games"
      use="_id"
      :display="e => { return `${e.name}`; }"
      :to="(id) => { return `/blade/games/${id}/edit`; }"
    >
      Singleplayer Alternative Gamulation (optional)
    </select-input>

    <collapsible-section
      name="Hub Thumbnail Settings"
      id="HTS"
      bold
      :buttonStyle="{
        'font-size': '110%'
      }"
    >
      <text-input label v-model.trim="game.thumbnailTitle">Thumbnail Title</text-input>

      <text-input label v-model.trim="game.description" textarea>Description</text-input>

      <text-input label v-model.trim="game.thumbnailResumeCompletedTextOverride">"Resume (Completed)" Button Text Override</text-input>

      <checkbox-input v-model="game.thumbnailBrandingOverride">Thumbnail Branding Override</checkbox-input>

      <img v-if="game.thumbnailImage" class="form-image-background" :src="resolveAssetPath(game.thumbnailImage, 'ui/customer')">
      <asset-select-input label v-model="game.thumbnailImage" legacy-manifest-path="ui/customer">Thumbnail Image</asset-select-input>

      <div
        v-if="game.thumbnailTextColor != null && game.thumbnailTextColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${game.thumbnailTextColor};`"
      />
      <text-input
        label
        v-model.trim="game.thumbnailTextColor"
        placeholder="e.g. white (if unspecified, defaults to white)"
      >
        Thumbnail Title Text Color (supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="game.thumbnailBackgroundColor != null && game.thumbnailBackgroundColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${game.thumbnailBackgroundColor};`"
      />
      <text-input
        label
        v-model.trim="game.thumbnailBackgroundColor"
        placeholder="e.g. rgba(17, 17, 17, 0.5) (if unspecified, falls back to hub's primary lightened color)"
      >
        Thumbnail Title Background Color (supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="game.detailsPreviewTextColor != null && game.detailsPreviewTextColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${game.detailsPreviewTextColor};`"
      />
      <text-input
        label
        v-model.trim="game.detailsPreviewTextColor"
        placeholder="e.g. white (if unspecified, defaults to white)"
      >
        Thumbnail Details Preview Text Color (supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="game.detailsPreviewBackgroundColor != null && game.detailsPreviewBackgroundColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${game.detailsPreviewBackgroundColor};`"
      />
      <text-input
        label
        v-model.trim="game.detailsPreviewBackgroundColor"
        placeholder="e.g. rgba(17, 17, 17, 0.5) (if unspecified)"
      >
        Thumbnail Details Preview Background Color (supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="game.detailsModalTextColor != null && game.detailsModalTextColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${game.detailsModalTextColor};`"
      />
      <text-input
        label
        v-model.trim="game.detailsModalTextColor"
        placeholder="e.g. white (if unspecified, defaults to white)"
      >
        Thumbnail Details Modal Text Color (supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <div
        v-if="game.detailsModalBackgroundColor != null && game.detailsModalBackgroundColor.length > 0"
        :style="`height: 15px; width: 15px; background-color: ${game.detailsModalBackgroundColor};`"
      />
      <text-input
        label
        v-model.trim="game.detailsModalBackgroundColor"
        placeholder="e.g. rgba(17, 17, 17, 0.5) (if unspecified)"
      >
        Thumbnail Details Modal Background Color (supports all valid CSS colors, but rgba() recommended)
      </text-input>

      <img v-if="game.detailsImage" class="form-image-background" :src="resolveAssetPath(game.detailsImage, 'ui/customer')">
      <asset-select-input label v-model="game.detailsImage" legacy-manifest-path="ui/customer">Details Image</asset-select-input>

      <img v-if="game.detailsLogo" class="form-image-background" :src="resolveAssetPath(game.detailsLogo, 'ui/customer')">
      <asset-select-input label v-model="game.detailsLogo" legacy-manifest-path="ui/customer">Details Logo</asset-select-input>
    </collapsible-section>

    <collapsible-section
      name="Game Start Screen Settings"
      id="GSSS"
      bold
      :buttonStyle="{
        'font-size': '110%'
      }"
    >
      <img v-if="game.titleImage" class="form-image-background" :src="resolveAssetPath(game.titleImage, 'ui/customer')">
      <asset-select-input label v-model="game.titleImage" legacy-manifest-path="ui/customer">Title Image</asset-select-input>

      <text-input label v-model.trim="game.startButtonText" placeholder="Start">
        Start Button Text
      </text-input>
    </collapsible-section>

    <text-input label v-model.trim="game.nextButtonDefaultText" placeholder="Next">
      Next Button Default Text
    </text-input>

    <asset-select-input label v-model="game.nextButtonSound">
      Next Button Sound
    </asset-select-input>

    <asset-select-input label v-model="game.backButtonSound">
      Back Button Sound
    </asset-select-input>

    <checkbox-input v-model="game.enableBack">Enable Backing Up Through Narratives</checkbox-input>

    <text-input v-if="game.enableBack" label v-model.trim="game.backButtonDefaultText" placeholder="Back">
      Back Button Default Text (for backing up through narratives)
    </text-input>

    <select-input label v-model="game.environment" :options="environments" use="_id" :display="e => { return `${e.name}`; }" :to="(id) => { return `/blade/environments/${id}/edit`; }">Gamulation Branding</select-input>

    <select-input
      label
      v-model="game.theme"
      :options="themes"
      :validator="validator.theme"
      required
      :display="o => ((o === 'starfighter') ? 'panoramic' : o)"
    >
      Theme
    </select-input>

    <character-select-input
      label
      select-only-id
      v-model="game.narrator"
      :default-filters="{narrator: true}"
      use="_id"
      display="name"
      :validator="validator.narrator"
      required
      :to="canManageGames ? ((id) => { return `/blade/characters/${id}/edit`; }) : undefined"
    >
      Narrator
    </character-select-input>

    <img v-if="game.resultsBackground && !game.playerResultsBackground" class="form-image-background" :src="resolveAssetPath(game.resultsBackground, 'backgrounds')">
    <div class="form-row">
      <checkbox-input class="col-sm-4" v-model="game.playerResultsBackground">Player Background for Results</checkbox-input>
      <asset-select-input v-if="!game.playerResultsBackground" class="col-sm-6" v-model="game.resultsBackground" :validator="validator.resultsBackground" required legacy-manifest-path="backgrounds">Results Background</asset-select-input>
    </div>

    <text-input label v-model.trim="game.reportTitle">Report Title (if unspecified, uses the default for the report type)</text-input>

    <number-input
      label
      min-value="0"
      max-value="1000"
      v-model="game.reasoningMinimumLength"
      :validator="validator.reasoningMinimumLength"
    >
      Reasoning Minimum Length in Characters
    </number-input>

    <number-input
      label
      min-value="0"
      max-value="1000"
      v-model="game.reasoningMaximumLength"
      :validator="validator.reasoningMaximumLength"
    >
      Reasoning Maximum Length in Characters (0 or blank will default to 256)
    </number-input>

    <checkbox-input v-model="game.hideProgressBar">Hide Progress Bar</checkbox-input>

    <checkbox-input v-model="game.hideDecisionHistory">Hide Activity Panel</checkbox-input>

    <checkbox-input v-model="game.demo">Active</checkbox-input>

    <checkbox-input v-model="game.completed">Completed</checkbox-input>

    <checkbox-input v-model="game.professor">Professor</checkbox-input>

    <asset-select-input label v-model="game.audioSelectBackground">
      Audio Track
    </asset-select-input>

    <checkbox-input v-if="game.audioSelectBackground" v-model="game.isAudioBackgroundLooping">Audio Track Looping</checkbox-input>

    <number-input v-if="game.audioSelectBackground" label v-model="game.backgroundAudioVolume">
      Audio Track Volume (percentage, only values from 1 to 100 are supported; 0 means full volume)
    </number-input>

    <asset-select-input label v-model="game.clickSoundOverride">
      Click Sound Override
    </asset-select-input>

    <asset-select-input label v-model="game.startButtonSound">
      Start Button Sound
    </asset-select-input>

    <asset-select-input label v-model="game.endButtonSound">
      End Button Sound
    </asset-select-input>

    <asset-select-input label v-model="game.arrowButtonSound">
      Arrow Button Sound
    </asset-select-input>

    <asset-select-input label v-model="game.resultsAnimationTickSound">
      Results Animation Tick Sound
    </asset-select-input>
  </collapsible-section>

  <template v-if="canManageGames">
  <collapsible-section
    v-if="isOwner"
    name="Gamulation Voice Line Bulk Generation"
    id="GVLBG"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <voice-config-editor
      :config="game"
      fallback
    ></voice-config-editor>
    <button
      class="btn"
      @click.prevent="() => generateOrOverwriteVoiceLinesForGame(game)"
      style="padding: 0 5px;"
    >
      Generate All TTS Voice Lines For Gamulation And Overwrite Any Existing Voice Lines
    </button>
    <div
      v-if="voiceLineBulkGenerationInProgress"
      class="progress-bar"
      style="
        background-color: white;
        width: 90%;
        height: 21px;
        border: 2px solid black;
        border-radius: 4px;
        position: relative;
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: center;
        margin: 13px 0;
      "
    >
      <div
        style="
          background-color: lightgreen;
          height: 100%;
          border-radius: 4px;
          transition: all 0.36s ease-in-out;
        "
        :style="{
          width: `${voiceLineBulkGenerationProgressPercentage}%`,
        }"
      ></div>
      <div
        style="
          position: absolute;
          bottom: -3px;
          right: 50%;
        "
      >{{voiceLineBulkGenerationProgressPercentage}}%</div>
    </div>
  </collapsible-section>
  <collapsible-section
    name="Gamulation Intro Narrative"
    id="GIN"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="game.introNarratives"
      :validator="validator.introNarratives"
      :characters="characters"
      :variants="variants"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      no-player-avatar
      no-player-role
      no-title
    >Gamulation Intro Narrative</narrative-input>
  </collapsible-section>

  <collapsible-section
    name="Avatar Select Settings"
    id="ASS"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <collapsible-section
      name="Backstories"
      id="ASS-B"
      bold
      :buttonStyle="{
        'font-size': '140%'
      }"
    >
      <array-input
        :items="game.backstories"
        :stub="{ challenges: [] }"
        :disabled="validator.backstories.$invalid && game.backstories.length > 0"
      >
        <h5 slot="title"></h5>

        <div class="form-row" slot="item" slot-scope="{ item: backstory, index: i }">
          <div style="width: 100%;">
            <text-input
              label
              v-model.trim="backstory.title"
              :validator="validator.backstories.$each[i].title"
              required
              :up="() => up(game.backstories, i)"
              :down="() => down(game.backstories, i)"
              :remove="() => { game.backstories.splice(i, 1); }"
            >
              Backstory Title
            </text-input>
            <text-input
              label
              textarea
              v-model.trim="backstory.description"
              :validator="validator.backstories.$each[i].description"
              required
            >
              Backstory Description
            </text-input>
            <div style="font-weight: bold; font-size: 93%;">
              NOTE: Backstories are currently only compatible with multiplayer if they do not affect the Section list.
            </div>
            <checkbox-input
              v-model="backstory.replace"
            >
              Section list below replaces the gamulation's Section list instead of prepending
            </checkbox-input>
            <array-input
              :items="backstory.challenges"
              :disabled="validator.backstories.$each[i].challenges.$invalid && backstory.challenges.length > 0"
              :hide-button="backstory.challenges.length >= challenges.length"
              :validator="validator.backstories.$each[i].challenges"
            >
              <div slot="title">Sections <span style="font-size: 85%;">(optional, {{backstory.replace ? 'replaces' : 'prepended before'}} gamulation's Section list if the player selects this backstory)</span></div>

              <div class="form-row" slot="item" slot-scope="{ item: challenge, index: j }">
                <select-input
                  v-model="challenge._id"
                  :validator="validator.backstories.$each[i].challenges.$each[j]._id"
                  :options="unused(challenges).by('_id').in(backstory.challenges).by('_id').at(j)"
                  use="_id"
                  display="name"
                  required
                  autofocus
                  :up="() => up(backstory.challenges, j)"
                  :down="() => down(backstory.challenges, j)"
                  :remove="() => { backstory.challenges.splice(j, 1); }"
                  :to="(id) => { return `/blade/challenges/${id}/edit`; }"
                >
                  Section
                </select-input>
              </div>
              <span slot="button">Add Section</span>
            </array-input>
          </div>
          <div style="height: 1px; background-color: lightgrey; width: calc(100% - 70px); margin: 0 35px 8px;"></div>
        </div>
        <span slot="button">Add Backstory</span>
      </array-input>
    </collapsible-section>

    <collapsible-section
      v-if="game.backstories && game.backstories.filter(g => g.title).length > 0"
      name="Backstory Select Display Settings"
      id="ASS-BSDS"
      bold
      :buttonStyle="{
        'font-size': '140%'
      }"
    >
      <checkbox-input v-model="game.backstorySelectAfterAvatarSelect">Backstory Select Happens After Avatar Select</checkbox-input>
      <checkbox-input
        v-if="game.backstorySelectAfterAvatarSelect"
        v-model="game.backstorySelectAfterAvatarSelectOutroNarrative"
      >Backstory Select Happens After Avatar Select Outro Narrative</checkbox-input>
      <checkbox-input
        v-if="game.backstorySelectAfterAvatarSelect"
        v-model="game.backstorySelectUsesNarrator"
      >Backstory Select Uses Narrator Instead Of Avatar</checkbox-input>
      <checkbox-input v-model="game.backstorySelectNoHeaders">Don't Display Backstory Titles</checkbox-input>

      <text-input
        label
        v-model.trim="game.backstorySelectPromptText"
        placeholder="Tell me about yourself. (Select your backstory.)"
      >
        Backstory Select - Prompt Text
      </text-input>

      <text-input
        label
        v-model.trim="game.backstorySelectContinueButtonText"
        placeholder="Continue"
      >
        Backstory Select - Continue Button Text
      </text-input>

      <img v-if="game.backstorySelectBackground" class="form-image-background" :src="resolveAssetPath(game.backstorySelectBackground, 'backgrounds')">

      <asset-select-input label v-model="game.backstorySelectBackground" legacy-manifest-path="backgrounds">
        Backstory Select - Background (uses Avatar Select - Background if unspecified)
      </asset-select-input>

      <h6>{{ backstorySelectCharacter(game) }}</h6>
      <div class="form-row">
        <select-input
          v-model="game.backstorySelectNarratorVariant"
          :options="variants"
        >
          {{ backstorySelectCharacter(game) }} Variant
        </select-input>

        <collapsible-section
          :name="`${backstorySelectCharacter(game)} Offset Settings`"
          id="ASS-BSDS-NOS"
          bold
          :style="{
            margin: '0 0 0 10px',
          }"
        >
          <div style="display: flex; flex-direction: row;">
            <text-input
              label
              v-model.trim="game.backstorySelectNarratorXOffset"
              placeholder="X Offset (e.g. 50px)"
              no-count
            >
              X Offset (use CSS units)
            </text-input>
            <text-input
              label
              v-model.trim="game.backstorySelectNarratorYOffset"
              placeholder="Y Offset (e.g. 50px)"
              no-count
            >
              Y Offset (use CSS units)
            </text-input>
          </div>
        </collapsible-section>
      </div>
    </collapsible-section>

    <collapsible-section
      name="Avatar Groups"
      id="ASS-AG"
      bold
      :buttonStyle="{
        'font-size': '140%'
      }"
    >
      <array-input :items="game.ageGroups" :disabled="validator.ageGroups.$invalid && game.ageGroups.length > 0">
        <h5 slot="title"></h5>

        <div class="form-row" slot="item" slot-scope="{ item: ageGroup, index: i }">
          <img v-if="ageGroup.image" class="form-image-background" :src="resolveAssetPath(ageGroup.image, 'ui/customer')">
          <asset-select-input label v-model="ageGroup.image" :validator="validator.ageGroups.$each[i].image" required legacy-manifest-path="ui/customer">
            Image
          </asset-select-input>
          <text-input label v-model.trim="ageGroup.label" :validator="validator.ageGroups.$each[i].label" required :remove="() => { game.avatars.forEach((a) => { if (a.ageGroup === game.ageGroups[i].label) { a.ageGroup = null } }); game.ageGroups.splice(i, 1); }">
            Label
          </text-input>
        </div>
        <span slot="button">Add Avatar Group</span>
      </array-input>
    </collapsible-section>

    <collapsible-section
      name="Avatars"
      id="ASS-A"
      bold
      :buttonStyle="{
        'font-size': '140%'
      }"
    >
      <array-input
        :items="game.avatars"
        :disabled="validator.avatars.$invalid && game.avatars.length > 0"
        :hide-button="game.avatars.length >= characters.filter(c => c.avatar).length"
        required
      >
        <h5 slot="title">
          <span v-if="game.ageGroups && game.ageGroups.filter(g => g.label).length > 0" style="font-size: 75%; margin-left: 10px;">
            (Avatar Group can be left blank to show up in all groups)
          </span>
        </h5>
        <div class="form-row" slot="item" slot-scope="{ item: avatar, index: i }">
          <!-- TODO: Refactor to <character-select-input>? -->
          <character-select-input
            v-model="avatar._id"
            select-only-id
            :validator="validator.avatars.$each[i]._id"
            :filterCallback="(c)=>c.avatar && (c._id === avatar._id || !game.avatars.some(a=>a._id === c._id))"
            :options="unused(characters.filter(c => c.avatar)).by('_id').in(game.avatars).by('_id').at(i)"
            use="_id"
            display="name"
            required
            autofocus
            :to="(id) => { return `/blade/characters/${id}/edit`; }"
            :remove="(game.ageGroups && game.ageGroups.filter(g => g.label).length > 0) ? undefined : (() => { game.avatars.splice(i, 1); })"
          >
            Avatar
          </character-select-input>
          <!-- <select-input
            v-model="avatar._id"
            :validator="validator.avatars.$each[i]._id"
            :options="unused(characters.filter(c => c.avatar)).by('_id').in(game.avatars).by('_id').at(i)"
            use="_id"
            display="name"
            required
            autofocus
            :to="(id) => { return `/blade/characters/${id}/edit`; }"
            :remove="(game.ageGroups && game.ageGroups.filter(g => g.label).length > 0) ? undefined : (() => { game.avatars.splice(i, 1); })"
          >
            Avatar
          </select-input> -->
          <select-input
            v-if="game.ageGroups && game.ageGroups.filter(g => g.label).length > 0"
            v-model="avatar.ageGroup"
            :options="game.ageGroups.filter(g => g.label)"
            use="label"
            display="label"
            :remove="(game.ageGroups && game.ageGroups.filter(g => g.label).length > 0) ? (() => { game.avatars.splice(i, 1); }) : undefined"
          >
            Avatar Group
          </select-input>
        </div>
        <span slot="button">Add Avatar</span>
      </array-input>
    </collapsible-section>

    <collapsible-section
      name="Avatar Select Display Settings"
      id="ASS-ASDS"
      bold
      :buttonStyle="{
        'font-size': '140%'
      }"
    >
      <text-input
        label
        v-model.trim="game.avatarSelectTitleText"
        placeholder="SELECT YOUR CHARACTER"
      >
        Avatar Select - Title Text
      </text-input>

      <text-input
        v-if="game.ageGroups && game.ageGroups.filter(g => g.label).length > 0"
        label
        v-model.trim="game.avatarSelectGroupSelectTitleText"
        placeholder="SELECT YOUR AGE"
      >
        Avatar Select - Group Select Title Text
      </text-input>

      <text-input
        v-if="game.ageGroups && game.ageGroups.filter(g => g.label).length > 0"
        label
        v-model.trim="game.avatarSelectGroupSelectPromptText"
        placeholder="What age would you like your character to be?"
      >
        Avatar Select - Group Select Prompt Text
      </text-input>

      <text-input
        v-if="game.ageGroups && game.ageGroups.filter(g => g.label).length > 0"
        label
        v-model.trim="game.avatarSelectGroupSelectBackButtonText"
        placeholder="Change Age"
      >
        Avatar Select - Group Select Back Button Text
      </text-input>

      <text-input
        label
        v-model.trim="game.avatarSelectButtonText"
        placeholder="Select"
      >
        Avatar Select - Button Text
      </text-input>

      <asset-select-input label v-model="game.avatarSelectArrowButtonSound">
        Avatar Select - Arrow Button Sound
      </asset-select-input>

      <img v-if="game.avatarSelectBackground" class="form-image-background" :src="resolveAssetPath(game.avatarSelectBackground, 'backgrounds')">

      <asset-select-input label v-model="game.avatarSelectBackground" :validator="validator.avatarSelectBackground" legacy-manifest-path="backgrounds" required>
        Avatar Select - Background
      </asset-select-input>

      <collapsible-section
        name="Tooltips"
        id="ASS-ASDS-Tooltips"
        bold
        :buttonStyle="{
          'font-size': '120%'
        }"
      >
        <tooltip-input
          :tooltips="game.avatarSelectTooltips"
          :target-types="tooltipTargetTypes"
          no-title
        >Tooltips</tooltip-input>
      </collapsible-section>
    </collapsible-section>
  </collapsible-section>

  <collapsible-section
    name="Avatar Select Outro Narrative"
    id="ASON"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="game.avatarSelectOutroNarratives"
      :validator="validator.avatarSelectOutroNarratives"
      :characters="characters"
      :variants="variants"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      no-player-role
      no-title
    >Avatar Select Outro Narrative</narrative-input>
  </collapsible-section>

  <collapsible-section
    name="Roles"
    id="R"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <array-input :items="game.roles" :disabled="validator.roles.$invalid && game.roles.length > 0" :hide-button="game.roles.length >= roles.length" required>
      <h5 slot="title"></h5>

      <div class="form-row" slot="item" slot-scope="{ item: role, index: i }">
        <select-input
          v-model="role._id"
          :validator="validator.roles.$each[i]._id"
          :options="unused(roles).by('_id').in(game.roles).by('_id').at(i)"
          use="_id"
          display="name"
          required
          autofocus
          :remove="() => { game.roles.splice(i, 1); }"
          :to="(id) => { return `/blade/roles/${id}/edit`; }"
        >
          Role
        </select-input>
        <checkbox-input v-model="role.required" style="margin-left: 15px;">Required</checkbox-input>
      </div>
      <span slot="button">Add Role</span>
    </array-input>
  </collapsible-section>

  <collapsible-section
    name="Role Select Outro Narrative"
    id="RSON"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="game.roleSelectOutroNarratives"
      :validator="validator.roleSelectOutroNarratives"
      :characters="characters"
      :variants="variants"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      no-title
    >Role Select Outro Narrative</narrative-input>
  </collapsible-section>

  <collapsible-section
    name="Starting Metrics"
    id="SM"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <checkbox-input v-model="game.carryOverMetricsFromActive">Carry over metrics as completed gamulation (or carry over metrics from Active-flag gamulation)</checkbox-input>

    <number-input label v-model="game.startingCash">Starting Cash</number-input>

    <array-input
      :items="game.startingTeamMetrics"
      :disabled="validator.startingTeamMetrics.$invalid"
      :hide-button="game.startingTeamMetrics.length >= metrics.filter(m => m.type === 'team').length"
    >
      <h5 slot="title">Starting Team Metrics</h5>

      <div class="form-row" slot="item" slot-scope="{ item: startingMetric, index: i }">
        <select-input
          class="col-sm-6"
          v-model="startingMetric._id"
          :validator="validator.startingTeamMetrics.$each[i]._id"
          :options="metrics"
          :filter="usedTeamMetrics"
          optimized
          use="_id"
          :display="m => { return `${m.name} (${m.type})`; }"
          required
          autofocus
          :to="(id) => { return `/blade/metrics/${id}/edit`; }"
        >
          Metric
        </select-input>
        <number-input class="col-sm-3" v-model="startingMetric.value" :validator="validator.startingTeamMetrics.$each[i].value" required :remove="() => { game.startingTeamMetrics.splice(i, 1); }">
          Value
        </number-input>
        <checkbox-input class="col-sm-2" v-model="startingMetric.hidden">
          Hidden
        </checkbox-input>
      </div>
      <span slot="button">Add Team Metric</span>
    </array-input>

    <array-input
      :items="game.startingIndividualMetrics"
      :disabled="validator.startingIndividualMetrics.$invalid"
      :hide-button="game.startingIndividualMetrics.length >= metrics.filter(m => m.type === 'individual').length"
    >
      <h5 slot="title">Starting Individual Metrics</h5>

      <div class="form-row" slot="item" slot-scope="{ item: startingMetric, index: i }">
        <select-input
          class="col-sm-6"
          v-model="startingMetric._id"
          :validator="validator.startingIndividualMetrics.$each[i]._id"
          :options="metrics"
          :filter="usedIndividualMetrics"
          optimized
          use="_id"
          :display="m => { return `${m.name} (${m.type})`; }"
          required
          autofocus
          :to="(id) => { return `/blade/metrics/${id}/edit`; }"
        >Metric</select-input>

        <number-input class="col-sm-3" v-model="startingMetric.value" :validator="validator.startingIndividualMetrics.$each[i].value" required :remove="() => { game.startingIndividualMetrics.splice(i, 1); }">Value</number-input>

        <checkbox-input class="col-sm-2" v-model="startingMetric.hidden">Hidden</checkbox-input>
      </div>
      <span slot="button">Add Individual Metric</span>
    </array-input>
  </collapsible-section>

  <collapsible-section
    name="Metric Display Settings"
    id="mds"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <checkbox-input v-model="game.showCashInReports">Show Cash In {{classTypeName}} Reports By Default</checkbox-input>

    <text-input
      label
      v-model.trim="game.cashTopBarLabel"
    >
      Cash Top Bar Label (Optional) (supports HTML/CSS, defaults to CASH RESERVE)
    </text-input>

    <div class="form-row">
      <text-input
        label
        v-model.trim="game.cashTopBarValueStyle"
        placeholder="e.g. font-size: 42px; color: white;"
      >
        Cash Top Bar Value Style (Optional) (CSS inline style value)
      </text-input>
      <collapsible-section
        name="Font Size Guide"
        id="mds-fsg"
        :style="{
          position: 'relative',
          top: '17px',
          left: '5px',
        }"
      >
        <span>For cash in the 10 millions, leave default.</span><br>
        <span>For cash in the 100 millions, font-size: 38px;</span><br>
        <span>For cash in the billions, font-size: 33px;</span><br>
        <span>For cash in the 10 billions, font-size: 30px;</span><br>
        <span>For cash in the 100 billions, font-size: 28px;</span><br>
      </collapsible-section>
    </div>

    <checkbox-input v-model="game.hideMetricsFromDecisionHistory">Hide Metrics From Activity</checkbox-input>

    <text-input
      label
      v-model.trim="game.metricTopBarLabelStyle"
      placeholder="e.g. font-size: 50px; color: black; font-family: 'Aller';"
    >
      Metric Top Bar Label Style (Optional) (CSS inline style value)
    </text-input>

    <text-input
      label
      v-model.trim="game.metricTopBarPercentageStyle"
      placeholder="e.g. font-size: 63px; color: black; font-family: 'Aller';"
    >
      Metric Top Bar Percentage Style (Optional) (CSS inline style value)
    </text-input>

    <text-input
      label
      v-model.trim="game.metricResultsLabelStyle"
      placeholder="e.g. font-size: 15.5px; line-height: 16.75px; color: black; font-family: 'Aller';"
    >
      Metric Results Label Style (Optional) (CSS inline style value)
    </text-input>

    <text-input
      label
      v-model.trim="game.metricResultsPercentageStyle"
      placeholder="e.g. font-size: 60px; color: black; font-family: 'Aller';"
    >
      Metric Results Percentage Style (Optional) (CSS inline style value)
    </text-input>

    <text-input
      label
      v-model.trim="game.metricReportLabelStyle"
      placeholder="e.g. font-size: 0.5em; color: black; font-family: 'Aller';"
    >
      Metric Report Label Style (Optional) (CSS inline style value)
    </text-input>

    <text-input
      label
      v-model.trim="game.metricReportPercentageStyle"
      placeholder="e.g. font-size: 63px; color: black; font-family: 'Aller';"
    >
      Metric Report Percentage Style (Optional) (CSS inline style value)
    </text-input>
  </collapsible-section>

  <collapsible-section
    name="Section Settings"
    id="SS"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <template v-if="game && game._id">
      <div class="form-row">
        <text-input
          class="col-sm-5"
          label
          v-model.trim="game.lastSyncedSheetId"
        >Google Sheet ID</text-input>
        <a
          v-if="game.lastSyncedSheetId"
          class="btn"
          style="height: 38px; margin: 32px 4px 0 4px;"
          :href="`https://docs.google.com/spreadsheets/d/${game.lastSyncedSheetId}`"
          target="_blank"
        >Open</a>
        <button
          class="btn"
          style="height: 38px; margin: 32px 0 0 4px;"
          :disabled="!(game.lastSyncedSheetId)"
          @click.prevent="confirmAndSyncWithGoogleSheets(game)"
        >Sync And Update</button>
        <div
          class="col-sm-2"
          style="
            display: flex;
            flex-flow: column nowrap;
            justify-content: end;
            align-items: start;
            padding: 16px;
            font-size: 80%;
          "
        >
          <div>Last Synced With Sheet:</div>
          <div>
            {{ (game && game.lastSyncedTimestamp) ? (new Date(game.lastSyncedTimestamp)).toLocaleString() : 'Never' }}
          </div>
        </div>
      </div>
    </template>

    <array-input :items="game.challenges" :disabled="validator.challenges.$invalid && game.challenges.length > 0" :hide-button="game.challenges.length >= challenges.length" :validator="validator.challenges" required>
      <h5 slot="title">Sections</h5>

      <div class="form-row" slot="item" slot-scope="{ item: challenge, index: i }">
        <select-input
          v-model="challenge._id"
          :validator="validator.challenges.$each[i]._id"
          :options="unused(challenges).by('_id').in(game.challenges).by('_id').at(i)"
          use="_id"
          display="name"
          required
          autofocus
          :remove="() => { game.challenges.splice(i, 1); }"
          :to="(id) => { return `/blade/challenges/${id}/edit`; }"
        >
          Section
        </select-input>
        <checkbox-input
          v-if="game.randomChallengeOrder"
          v-model="challenge.fixed"
          style="margin-left: 15px;"
        >
          Fixed Order
        </checkbox-input>
      </div>
      <span slot="button">Add Section</span>
    </array-input>

    <checkbox-input v-model="game.doFirstChallengeBeforeIntro">Do First Section Before Intro <span style="font-size: 80%;">(requires 2+ Sections to work correctly)</span></checkbox-input>

    <checkbox-input v-model="game.randomChallengeOrder">Randomize Section Order</checkbox-input>

    <checkbox-input v-model="game.carryOverMetricsBetweenChallenges">Carry over metrics between sections</checkbox-input>
  </collapsible-section>

  <collapsible-section
    name="Interrupting Event Pool Settings"
    id="EPS"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <select-input
      v-model="game.interruptingEventPool"
      label
      :options="eventPools"
      use="_id"
      display="name"
      required
      autofocus
      :to="(id) => { return `/blade/event-pools/${id}/edit`; }"
    >
      Interrupting Event Pool
    </select-input>

    <checkbox-input v-model="game.enablePostIntroInterrupt">Interrupting Events Can Interrupt After Decision Intro</checkbox-input>

    <checkbox-input v-model="game.enablePreDecisionInterrupt">Interrupting Events Can Interrupt Before Decision Intro</checkbox-input>
  </collapsible-section>

  <collapsible-section
    name="Gamulation Outro Narrative"
    id="GON"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="game.outroNarratives"
      :validator="validator.outroNarratives"
      :characters="characters"
      :variants="variants"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      no-title
    >Gamulation Outro Narrative</narrative-input>
  </collapsible-section>

  <collapsible-section
    name="Gamulation Endgame"
    id="GE"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <select-input
      label
      v-model="game.completedGame"
      @input="() => { if (game.completedGame != null) { game.outro = null; } }"
      :options="games"
      use="_id"
      :display="e => `${e.name}`"
      :to="(id) => `/blade/games/${id}/edit`"
    >
      Gamulation To Run When This Gamulation Is Completed
    </select-input>

    <checkbox-input
      v-if="game.completedGame"
      v-model="game.assignCompletedGame"
    >Assign Above Gamulation When This Gamulation Is Completed (shows up on home page)</checkbox-input>

    <select-input
      label
      v-model="game.outro"
      @input="() => { if (game.outro != null) { game.completedGame = null; } }"
      :options="outros"
      use="_id"
      :display="e => { return `${e.name}`; }"
      :to="(id) => { return `/blade/outros/${id}/edit`; }"
    >
      Endgame Settings (runs when Gamulation ends, repeatable, overrides Completed Gamulation)
    </select-input>

    <text-input
      label
      v-model.trim="game.gameEndButtonText"
      placeholder="End"
    >
      Gamulation End Button Text
    </text-input>

    <text-input
      label
      v-model.trim="game.gameEndContinueButtonText"
      placeholder="Continue"
    >
      Gamulation End External Survey Button Text (default: Continue)
    </text-input>

    <character-select-input
      label
      select-only-id
      v-model="game.gameEndCharacter"
      :default-filters="{ narrator: true, avatar: true }"
      use="_id"
      display="name"
      :to="(id) => { return `/blade/characters/${id}/edit`; }"
    >
      Gamulation End Screen Character (defaults to player avatar)
    </character-select-input>

    <select-input
      label
      v-model="game.gameEndCharacterVariant"
      :options="variantOptions(characters, variants, game.gameEndCharacter)"
    >
      Gamulation End Screen Character Variant (defaults to Forward Right)
    </select-input>

    <img
      v-if="game.gameEndBackground"
      class="form-image-background"
      :src="resolveAssetPath(game.gameEndBackground, 'backgrounds')"
    >
    <asset-select-input
      class="col-sm-6"
      v-model="game.gameEndBackground"
      legacy-manifest-path="backgrounds"
    >Gamulation End Background</asset-select-input>
  </collapsible-section>

  <collapsible-section
    name="Gamulation Dashboard Settings"
    id="GDS"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
    v-if="canManageDashboards"
  >
    <!--
      Only available for existing games because we're grabbing the target game during the fetch
      This is kind of hacky, but the edit page system is extremely annoying to use with computed
      properties, so... yeah.
    -->
    <template v-if="!creating">
      <array-input
        v-if="validDashboardProfiles.length > 0"
        :items="game.dashboardProfiles"
        :hide-button="game.dashboardProfiles.length === validDashboardProfiles.length"
        :validator="validator.dashboardProfiles"
      >
        <h5 slot="title">Dashboard Profiles</h5>

        <div class="form-row" slot="item" slot-scope="{ item: profile, index: i }">
          <select-input
            v-model="profile._id"
            :validator="validator.dashboardProfiles.$each[i]._id"
            :options="unused(validDashboardProfiles).by('_id').in(game.dashboardProfiles).by('_id').at(i)"
            use="_id"
            display="name"
            required
            autofocus
            :remove="() => { game.dashboardProfiles.splice(i, 1); }"
            :to="(id) => { return `/blade/dashboards/profiles/${id}/edit`; }"
          >
            Dashboard Profile
          </select-input>
        </div>
        <span slot="button">Add Dashboard Profile</span>
      </array-input>
      <div v-else>
        <b-alert show variant="warning">No compatible dashboard profiles found</b-alert>
      </div>
      <!-- <b-alert v-if="validator.dashboardProfiles.$invalid && validator.dashboardProfiles.ensureSingleDashboardMapping.$invalid" show variant="error">
        Two dashboard profiles share the same dashboard.  Gamulations may only have a single profile for a given dashboard.
      </b-alert> -->
    </template>
    <template v-else>
      <b-alert show variant="warning">
        This section is only available while editing an existing gamulation.
        Please save the gamulation to modify dashboard settings.
      </b-alert>
    </template>
  </collapsible-section>
  </template>
  </template>
</template>
</edit-page>
</template>

<script>
import { required, requiredIf, maxValue, minValue } from 'vuelidate/lib/validators';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import { narrative } from '../../../utils/validators';
import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';
import NarrativeInput from '../../ui/inputs/NarrativeInput';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import NumberInput from '../../ui/inputs/NumberInput';
import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import CharacterSelectInput from '../../ui/inputs/CharacterSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';
import TooltipInput from '../../ui/inputs/TooltipInput';
import VoiceConfigEditor from './components/VoiceConfigEditor';

export default {
  components: {
    EditPage,
    NarrativeInput,
    ArrayInput,
    CheckboxInput,
    NumberInput,
    AssetSelectInput,
    CharacterSelectInput,
    SelectInput,
    TextInput,
    CollapsibleSection,
    TooltipInput,
    VoiceConfigEditor,
  },
  data() {
    return {
      utils: {
        unused
      },
      fetchedGame: null,
      gameStub: {
        _id: '',
        name: '',
        title: '',
        ticketPrefix: '',
        titleOverridesClass: false,
        description: '',
        startButtonText: '',
        nextButtonDefaultText: '',
        enableBack: false,
        titleImage: '',
        thumbnailTitle: '',
        thumbnailBrandingOverride: false,
        thumbnailImage: '',
        thumbnailTextColor: '',
        thumbnailBackgroundColor: '',
        detailsPreviewTextColor: '',
        detailsPreviewBackgroundColor: '',
        detailsModalTextColor: '',
        detailsModalBackgroundColor: '',
        detailsImage: '',
        detailsLogo: '',
        singleplayerGame: null,
        completedGame: null,
        assignCompletedGame: false,
        outro: null,
        environment: null,
        reportTitle: '',
        reasoningMinimumLength: 0,
        reasoningMaximumLength: 0,
        demo: false,
        completed: false,
        professor: false,
        carryOverMetricsFromActive: false,
        theme: 'starfighter',
        challenges: [],
        randomChallengeOrder: false,
        carryOverMetricsBetweenChallenges: false,
        eventPool: null,
        enablePostIntroInterrupt: false,
        enablePreDecisionInterrupt: false,
        roles: [],
        backstories: [],
        ageGroups: [],
        avatars: [],
        startingCash: 0,
        startingTeamMetrics: [],
        startingIndividualMetrics: [],
        hideProgressBar: false,
        hideDecisionHistory: false,
        hideMetricsFromDecisionHistory: false,
        narrator: '',
        gameEndCharacter: '',
        gameEndCharacterVariant: '',
        gameEndBackground: '',
        background: '',
        avatarSelectBackground: '',
        avatarSelectTitleText: 'SELECT YOUR CHARACTER',
        avatarSelectGroupSelectTitleText: 'SELECT YOUR AGE',
        avatarSelectGroupSelectPromptText: 'What age would you like your character to be?',
        avatarSelectGroupSelectBackButtonText: 'Change Age',
        backstorySelectBackground: '',
        backstorySelectPromptText: 'Tell me about yourself. (Select your backstory.)',
        backstorySelectContinueButtonText: 'Continue',
        backstorySelectNarratorVariant: '',
        backstorySelectNarratorXOffset: '',
        backstorySelectNarratorYOffset: '',
        backstorySelectNoHeaders: false,
        backstorySelectAfterAvatarSelect: false,
        backstorySelectAfterAvatarSelectOutroNarrative: false,
        backstorySelectUsesNarrator: false,
        introNarratives: [],
        outroNarratives: [],
        avatarSelectOutroNarratives: [],
        roleSelectOutroNarratives: [],
        avatarSelectTooltips: [],
        invites: []
      },
      dashboardProfiles: [],
      themes: [],
      gameUsers: [],
      currentPage: 1,
      perPage: 25,
      perPageInput: 25,
      filter: '',
      voiceLineBulkGenerationProgressPercentage: 0,
      voiceLineBulkGenerationInProgress: false,
      validator: {
        name: { required },
        title: { required },
        reasoningMinimumLength: { minValue: minValue(0), maxValue: maxValue(1000) },
        reasoningMaximumLength: { minValue: minValue(0), maxValue: maxValue(1000) },
        theme: { required },
        challenges: {
          required,
          $each: {
            _id: { required }
          }
        },
        roles: {
          required,
          $each: {
            _id: { required }
          }
        },
        dashboardProfiles: {
          // TODO: Set the following up later
          // ensureSingleDashboardMapping: (value) => {
          //   if (!value) return false;
          //   for (let i = 0; i < value.length; i++) {
          //     for (let ii = i + 1; ii < value.length; ii++) {
          //       if (value[i]._id === value[ii]._id) return false;
          //     }
          //   }
          //   return true;
          // },
          $each: {
            _id: { required }
          }
        },
        backstories: {
          $each: {
            title: { required },
            description: { required },
            challenges: {
              $each: {
                _id: { required }
              }
            },
          }
        },
        ageGroups: {
          $each: {
            image: { required },
            label: { required }
          }
        },
        avatars: {
          required,
          $each: {
            _id: { required }
          }
        },
        startingTeamMetrics: {
          $each: {
            _id: { required },
            value: { required }
          }
        },
        startingIndividualMetrics: {
          $each: {
            _id: { required },
            value: { required }
          }
        },
        narrator: { required },
        avatarSelectBackground: { required },
        resultsBackground: {
          required: requiredIf(({ playerResultsBackground }) => !playerResultsBackground)
        },
        introNarratives: narrative(),
        outroNarratives: narrative(),
        avatarSelectOutroNarratives: narrative(),
        roleSelectOutroNarratives: narrative()
      },
      tooltipTargetTypes: [
        'selectedAvatar',
        'progressBar',
      ],
    };
  },
  computed: {
    ...mapGetters([
      'isOwner',
      'canManageGames',
      'canManageDashboards',
      'canEditGameGeneralSettings',
      'canManageUsers',
      'classTypeName'
    ]),
    validDashboardProfiles() {
      const game = this.fetchedGame;
      if (!game) return [];

      return this.dashboardProfiles.filter(
        (profile) => {
          // eslint-disable-next-line no-restricted-syntax
          for (const mapping of profile.mappings) {
            // If we find a metric that is not in startingTeamMetrics or startingIndividualMetrics,
            // then the dashboard is not compatible.
            if (mapping.metricId && !(
              game.startingTeamMetrics.find(metric => metric._id === mapping.metricId) ||
              game.startingIndividualMetrics.find(metric => metric._id === mapping.metricId)
            )) {
              return false;
            }
          }
          return true;
        });
    },
    creating() {
      return !(this.$route.params.gameId);
    },
    tableFields() {
      const fields = [
        { key: 'lastName', sortable: true },
        { key: 'firstName', sortable: true },
        { key: 'email', sortable: true },
        { key: 'status', sortable: true },
        {
          key: 'lastSignedIn',
          sortable: true,
          sortByFormatted: false,
          formatter: (value, key, item) => this.computeCreationDateString(item.lastSignedIn)
        },
      ];
      return fields;
    },
    formattedUsers() {
      let intermediate = this.gameUsers;
      const trimmed = this.filter.trim().toLowerCase();
      if (trimmed !== '') {
        intermediate = intermediate.filter(a =>
          a.lastName.toLowerCase().includes(trimmed)
          || a.email.toLowerCase().includes(trimmed)
          || a.firstName.toLowerCase().includes(trimmed)
        );
      }
      return intermediate;
    },
    usedTeamMetrics() {
      return this.fetchedGame ? new Set(this.fetchedGame.startingTeamMetrics.map(m => m._id)) : null;
    },
    usedIndividualMetrics() {
      return this.fetchedGame ? new Set(this.fetchedGame.startingIndividualMetrics.map(m => m._id)) : null;
    },
  },
  methods: {
    ...mapMutations([
      'submitEditUpdate',
      'setRootLoadingMessage'
    ]),
    ...mapActions([
      'getGame',
      'getGameTypes',
      'getGameEnums',
      'getGameUsers',
      'getDashboardProfiles',
      'createGame',
      'updateGame',
      'deleteGame',
      'flashPrimary',
      'flashDanger',
      'syncGameWithGoogleSheet',
      'batchGenerateVoiceLines',
      'computeAllVoiceLineIdsToGenerateForGame',
      'getNumberOfPotentiallyGeneratedVoiceLinesInGame',
      'generateGameInvite'
    ]),
    async confirmAndSyncWithGoogleSheets(game) {
      // eslint-disable-next-line no-alert
      if (confirm('Sync this Gamulation with Google Sheet?')) {
        try {
          this.flashPrimary({ message: 'Syncing Gamulation with Google Sheet, please wait for automatic page refresh...' });
          await this.syncGameWithGoogleSheet(game);
          this.$router.go(0);
        } catch (err) {
          const message =
            (err && err.response && err.response.data)
              ? `Sheet Sync Unsuccessful: ${err.response.data}`
              : 'Sheet Sync Unsuccessful';
          this.flashDanger({ message });
          console.error(Object.assign({}, err));
        }
      }
    },
    async generateOrOverwriteVoiceLinesForGame(game) {
      console.log(game.fallbackGoogleTtsAudioConfig);
      if (!game.fallbackVoice) {
        // eslint-disable-next-line no-alert
        alert('Please set a fallback voice in the game settings.');
        return;
      }

      const numLines = await this.getNumberOfPotentiallyGeneratedVoiceLinesInGame({
        gameId: game._id,
        fallbackVoice: game.fallbackVoice,
        fallbackGoogleTtsAudioConfig: game.fallbackGoogleTtsAudioConfig,
      });

      if (numLines === 0 || numLines === '0') {
        // eslint-disable-next-line no-alert
        alert('All voice lines are up-to-date.');
        return;
      }
      // eslint-disable-next-line no-alert
      if (confirm(`Are you sure you want to generate all voice lines for this gamulation (${numLines}) and overwrite any existing lines? This may take a while. On completion, the page will refresh.`)) {
        // this.setRootLoadingMessage('Please wait. Generating voice lines...');
        this.voiceLineBulkGenerationInProgress = true;
        this.voiceLineBulkGenerationProgressPercentage = 0;
        try {
          await this.submitEditUpdate(); // saves the bulk generation settings
          const voiceLineIds = await this.computeAllVoiceLineIdsToGenerateForGame({
            gameId: game._id,
            fallbackVoice: game.fallbackVoice,
            fallbackGoogleTtsAudioConfig: game.fallbackGoogleTtsAudioConfig,
          });
          this.voiceLineBulkGenerationProgressPercentage = 5; // arbitrarily making the first step be 5%
          console.log('Need to generate voice lines for IDs:');
          console.log(voiceLineIds);
          const totalLines = voiceLineIds.length;
          let currentBatch = [];
          let completedLineGenerations = 0;
          let tries = 0;
          while (voiceLineIds.length > 0) {
            // eslint-disable-next-line no-plusplus
            if (++tries > 3) {
              console.log('Failed to generate voice lines. Too many failed tries.');
              // eslint-disable-next-line no-alert
              alert('Failed to generate voice lines. See browser console for details.');
              // this.setRootLoadingMessage('');
              return;
            }
            // TODO: using 10 because, with journey voices, 10 lines might take ~20-30s (worst-case delay for progress bar updates)
            currentBatch = voiceLineIds.slice(0, 10); // first 10 elements
            try {
              console.log('Generate for batch:');
              console.log(currentBatch);
              await this.batchGenerateVoiceLines({
                voiceLineIds: currentBatch,
              });
              completedLineGenerations += currentBatch.length;
              this.voiceLineBulkGenerationProgressPercentage =
                Math.min(100, 5 + Math.round((completedLineGenerations / totalLines) * 95));
              console.log(this.voiceLineBulkGenerationProgressPercentage);
              tries = 0;
              voiceLineIds.splice(0, currentBatch.length);
            } catch (err) {
              console.log(err);
            }
          }

          await new Promise(resolve => setTimeout(resolve, 500));
          // eslint-disable-next-line no-alert
          alert('Voice line generation complete. The page will now refresh.');
          this.voiceLineBulkGenerationInProgress = false; // technically unnecessary
          this.$router.go(0);
        } catch (err) {
          // eslint-disable-next-line no-alert
          alert('Failed to generate voice lines. See browser console for details.');
          this.voiceLineBulkGenerationInProgress = false;
          // this.setRootLoadingMessage('');
        }
      }
    },
    async fetchGame(arg) {
      const resp = await this.getGame(arg);
      if (resp.game) {
        // Caching for profile initialization
        this.fetchedGame = resp.game;
      }
      return resp;
    },
    alertWrapper(t) {
      /* eslint-disable-next-line no-alert */
      alert(t);
    },
    up(items, index) {
      if (index <= 0) return;
      const [item] = items.splice(index, 1);
      items.splice(index - 1, 0, item);
    },
    down(items, index) {
      if (index >= items.length - 1) return;
      const [item] = items.splice(index, 1);
      items.splice(index + 1, 0, item);
    },
    async generateInvite(game) {
      await this.generateGameInvite(game);
      this.$router.go(0); // refresh
    },
    clientInviteLink(game) {
      return (game.invites == null || game.invites.length === 0)
        ? 'No Invite Has Been Generated Yet'
        : `${window.location.origin}/signup/${game.invites[0].code}`;
    },
    computeCreationDateString(date) {
      if (!date) {
        return '';
      }
      return new Date(date).toLocaleString();
    },
    updatePerPage() {
      this.perPage = this.perPageInput;
    },
    variantOptions(characters, variants, characterId) {
      const c = characters.find(ch => ch._id === characterId);
      if (c) {
        if (c.imageVariants && c.imageVariants.length > 0) {
          return c.imageVariants.map(v => v.name);
        }
        return [];
      }
      return variants;
    },
    backstorySelectCharacter(game) {
      return (game.backstorySelectAfterAvatarSelect && !(game.backstorySelectUsesNarrator))
        ? 'Avatar'
        : 'Narrator';
    }
  },
  async mounted() {
    const { themes } = await this.getGameEnums();
    this.themes = themes;
    if (!(this.creating)) {
      this.gameUsers = await this.getGameUsers({ gameId: this.$route.params.gameId });
      if (this.canManageDashboards) {
        this.dashboardProfiles = await this.getDashboardProfiles();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@keyframes breathe {
  from {
    outline-width: 0px;
  }
  to {
    outline-width: 3px;
  }
}

.progress-bar {
  color: black;
  outline: 0px solid #bbffcc;
  animation: 0.5s linear 0s infinite alternate breathe;
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/GameEdit.vue